<script lang="ts">
  import { Link } from 'svelte-routing';
  import { onMount } from 'svelte';
  import { api } from '../../lib/api/client';
  import type { AuthSession, Device, ReusedSignInLink, SignInLink } from '../../lib/api/types';
  import { confirm } from '../../lib/stores/confirmDialog';
  import { age, clock } from '../../lib/utils/time';
  import { acting, managedBy } from '../../lib/stores/auth';
  import PageHeader from '../../lib/components/PageHeader.svelte';
  import SignInLinkCard from '../../lib/components/SignInLinkCard.svelte';
  import Quality from './Quality.svelte';
  import { monthDay, t } from '../../lib/i18n/index.ts';

  // around splits a message at the spots a link, field, or styled word fills, in order.
  const around = (message: string) => message.split('\u0000');
  const addTracker = around(t('settingsAddTracker', { link: '\u0000' }));

  let devices: Device[] = $state([]);
  // quality is the phone whose tracking quality report is open (spec 002).
  let quality: Device | null = $state(null);
  let reused: ReusedSignInLink[] = $state([]);
  let link: SignInLink | null = $state(null);
  let error = $state('');
  // sessions are the browsers and apps signed in to the account (spec 001
  // FR-119). Each shows once: an app's as its device, with its session's end
  // in the device's row, and a browser's under Sessions.
  let sessions: AuthSession[] = $state([]);
  const browsers = $derived(sessions.filter((s) => s.kind === 'web'));
  const deviceEnds = $derived(new Map(sessions.filter((s) => s.device_id).map((s) => [s.device_id!, s.expires_at])));

  onMount(load);

  async function load() {
    try {
      devices = await api.get<Device[]>('/me/devices');
      reused = await api.get<ReusedSignInLink[]>('/me/sign-in-links/reused');
      sessions = await api.get<AuthSession[]>('/auth/sessions');
    } catch (e: any) {
      error = e.message;
    }
  }

  async function newLink() {
    try {
      link = await api.post<SignInLink>('/auth/sign-in-links');
    } catch (e: any) {
      error = e.message;
    }
  }

  // makePrimary makes a device the one whose arrivals and departures the
  // member's groups hear of (003 FR-006).
  async function makePrimary(d: Device) {
    await api.post(`/devices/${d.id}/primary`).catch((e: any) => (error = e.message));
    await load();
  }

  async function signOut(d: Device) {
    if (!(await confirm(t('settingsSignOutConfirm', { device: d.name })))) return;
    await api.delete(`/devices/${d.id}`).catch((e: any) => (error = e.message));
    await load();
  }

  const platformIcon: Record<string, string> = { ios: 'la-apple', android: 'la-android', macos: 'la-apple', windows: 'la-windows', linux: 'la-linux' };

  // sessionName names a session by its device, or by its browser and system.
  function sessionName(s: AuthSession): string {
    if (s.kind === 'app') return s.device_name ?? t('sessionsBrowser');
    if (s.browser && s.system) return t('sessionsBrowserOn', { browser: s.browser, system: s.system });
    return s.browser || t('sessionsBrowser');
  }

  // ends says when a session ends if unused: the time today, else the day and time.
  function ends(iso: string): string {
    const d = new Date(iso);
    if (d.toDateString() === new Date().toDateString()) return clock(iso);
    return t('componentsWhenDate', { date: monthDay(d), time: clock(iso) });
  }

  async function endSession(s: AuthSession) {
    if (!(await confirm(t('sessionsSignOutConfirm', { name: sessionName(s) })))) return;
    await api.delete(`/auth/sessions/${s.id}`).catch((e: any) => (error = e.message));
    await load();
  }

  async function endOthers() {
    if (!(await confirm(t('sessionsSignOutOthersConfirm')))) return;
    await api.post('/auth/sessions/revoke-others').catch((e: any) => (error = e.message));
    await load();
  }
</script>

{#if error}
  <div class="mb-4 p-3 rounded text-sm" style="background-color: var(--theme-error); color: var(--theme-base);">{error}</div>
{/if}

<!-- A child's devices are their parents' to sign out, so they show read-only (spec 001 FR-098). -->
<fieldset disabled={!!$managedBy} class="min-w-0">
<div class="card p-5 mb-6">
  <PageHeader title={t('settingsPhones')} level="section">
    {#if !$acting}
      <button class="btn btn-secondary btn-sm" onclick={newLink}><i aria-hidden="true" class="las la-qrcode mr-1"></i>{t('settingsSignInPhone')}</button>
    {/if}
  </PageHeader>
  {#if link}
    <div class="mb-6"><SignInLinkCard {link} /></div>
  {/if}
  {#each reused as r (r.id)}
    <p class="text-sm text-error mb-3" role="alert">
      <i aria-hidden="true" class="las la-exclamation-triangle mr-1"></i>{t('settingsLinkReused', { when: age(r.created_at) })}
    </p>
  {/each}
  {#if devices.length > 1}
    <p class="text-sm text-text-muted mb-2">{t('settingsPrimaryNote')}</p>
  {/if}
  {#if devices.length === 0}
    <p class="text-sm text-text-muted">{t('settingsNoPhones')}</p>
  {:else}
    <ul>
      {#each devices as d (d.id)}
        <li class="py-2 flex items-center gap-3">
          {#if d.kind === 'tracker'}
            <i aria-hidden="true" class="las la-satellite text-xl"></i>
          {:else if d.kind === 'computer'}
            <i aria-hidden="true" class="las la-laptop text-xl"></i>
          {:else}
            <i aria-hidden="true" class="lab {platformIcon[d.platform] ?? 'la-mobile'} text-xl"></i>
          {/if}
          <span class="flex-1">
            <span class="font-medium">{d.name}</span>
            {#if d.primary && devices.length > 1}<span class="text-xs font-medium ml-2 px-2 py-0.5 rounded-full" style="background: var(--theme-surface0);">{t('settingsPrimary')}</span>{/if}
            {#if d.kind === 'tracker'}
              <span class="text-xs text-text-muted ml-2">{t('settingsTrackerSeen', { when: age(d.last_seen_at) })}</span>
            {:else if d.kind === 'computer'}
              <span class="text-xs text-text-muted ml-2">{d.app_version ? t('settingsComputerSeenVersion', { version: d.app_version, when: age(d.last_seen_at) }) : t('settingsComputerSeen', { when: age(d.last_seen_at) })}</span>
            {:else}
              <span class="text-xs text-text-muted ml-2">{d.flavor || d.app_version ? t('settingsPhoneSeenApp', { app: [d.flavor, d.app_version].filter(Boolean).join(' '), when: age(d.last_seen_at) }) : t('settingsPhoneSeen', { when: age(d.last_seen_at) })}</span>
            {/if}
            {#if deviceEnds.has(d.id)}
              <span class="text-xs text-text-muted"> · {t('sessionsEnds', { when: ends(deviceEnds.get(d.id)!) })}</span>
            {/if}
          </span>
          {#if d.platform === 'ios' || d.platform === 'android'}
            <button class="btn btn-secondary btn-sm" onclick={() => (quality = d)}>{t('settingsQuality')}</button>
          {/if}
          {#if d.kind === 'tracker'}
            <Link to="/devices" class="btn btn-secondary btn-sm">{t('settingsManage')}</Link>
          {:else}
            <!-- A computer is never primary (spec 011 FR-018). -->
            {#if !d.primary && devices.length > 1 && d.kind !== 'computer'}
              <button class="btn btn-secondary btn-sm" onclick={() => makePrimary(d)}>{t('settingsMakePrimary')}</button>
            {/if}
            <button class="btn btn-secondary btn-sm" onclick={() => signOut(d)}>{t('settingsSignOut')}</button>
          {/if}
        </li>
      {/each}
    </ul>
  {/if}
  <p class="text-sm mt-2">
    {#if devices.length > 1}<Link to="/devices" style="color: var(--theme-accent);">{t('settingsFindDevices')}</Link> · {/if}{addTracker[0]}<Link to="/devices?add=1" style="color: var(--theme-accent);">{t('settingsAddTrackerLink')}</Link>{addTracker[1]}
  </p>
</div>

<div class="card p-5 mb-6">
  <PageHeader title={t('sessionsTitle')} level="section">
    {#if sessions.some((s) => !s.current)}
      <button class="btn btn-danger btn-sm" onclick={endOthers}><i aria-hidden="true" class="las la-sign-out-alt mr-1"></i>{t('sessionsSignOutOthers')}</button>
    {/if}
  </PageHeader>
  <p class="text-sm text-text-muted mb-2">{t('sessionsAbout')}</p>
  <ul>
    {#each browsers as s (s.id)}
      <li class="py-2 flex items-center gap-3">
        <i aria-hidden="true" class="las {s.kind === 'web' ? 'la-globe' : s.device_kind === 'computer' ? 'la-laptop' : 'la-mobile'} text-xl"></i>
        <span class="flex-1 min-w-0">
          <span class="font-medium">{sessionName(s)}</span>
          {#if s.current}<span class="text-xs font-medium ml-2 px-2 py-0.5 rounded-full" style="background: var(--theme-surface0);">{t('sessionsThis')}</span>{/if}
          <span class="block text-xs text-text-muted">
            <span class="font-mono">{s.ip_address}</span> · {t('sessionsActive', { when: age(s.last_active_at) })} · {t('sessionsEnds', { when: ends(s.expires_at) })}
          </span>
        </span>
        {#if !s.current}
          <button class="btn btn-secondary btn-sm" onclick={() => endSession(s)}>{t('sessionsSignOut')}</button>
        {/if}
      </li>
    {/each}
  </ul>
</div>
</fieldset>
{#if quality}
  <Quality device={quality} onclose={() => (quality = null)} />
{/if}
